/**
 * **Reine Helfer für die Ablage** ([Z-14](../../../../docs/phasen/zusatz-14-ablage.md)) –
 * Baum bauen, Nachkommen zählen, Fassungen zu einer Datei zusammensetzen. Ohne React, damit
 * `ablage.test.ts` sie ohne Rahmen prüfen kann.
 */

export interface Knoten {
  id: string
  elternId: string | null
  titel: string
  sortOrder: number
}

export interface Baumknoten<T extends Knoten> {
  eintrag: T
  kinder: Baumknoten<T>[]
  tiefe: number
}

/**
 * **Aus der flachen Liste den Baum** – Reihenfolge je Ebene nach `sortOrder`, dann Titel.
 *
 * Ein Eintrag, dessen Eltern fehlt (gelöscht, während der Baum unterwegs war), landet oben
 * statt zu verschwinden: lieber ein Dokument an der falschen Stelle als eines, das niemand
 * mehr findet.
 */
export function baumBauen<T extends Knoten>(eintraege: readonly T[]): Baumknoten<T>[] {
  const vorhanden = new Set(eintraege.map((e) => e.id))
  const kinderVon = new Map<string | null, T[]>()
  for (const e of eintraege) {
    const eltern = e.elternId && vorhanden.has(e.elternId) ? e.elternId : null
    const liste = kinderVon.get(eltern)
    if (liste) liste.push(e)
    else kinderVon.set(eltern, [e])
  }
  const sortiert = (liste: T[]) =>
    [...liste].sort((a, b) => a.sortOrder - b.sortOrder || a.titel.localeCompare(b.titel, 'de'))

  const bauen = (eltern: string | null, tiefe: number, gesehen: Set<string>): Baumknoten<T>[] =>
    sortiert(kinderVon.get(eltern) ?? [])
      .filter((e) => !gesehen.has(e.id))
      .map((e) => {
        const weiter = new Set(gesehen).add(e.id)
        return { eintrag: e, tiefe, kinder: bauen(e.id, tiefe + 1, weiter) }
      })

  return bauen(null, 0, new Set())
}

/** Alle Kennungen unterhalb eines Knotens – für „… und 3 Dokumente darunter" und das Verschieben. */
export function nachkommen(eintraege: readonly Knoten[], id: string): string[] {
  const ergebnis: string[] = []
  const offen = [id]
  const gesehen = new Set<string>([id])
  while (offen.length > 0) {
    const aktuell = offen.pop()!
    for (const e of eintraege) {
      if (e.elternId === aktuell && !gesehen.has(e.id)) {
        gesehen.add(e.id)
        ergebnis.push(e.id)
        offen.push(e.id)
      }
    }
  }
  return ergebnis
}

/** Der Weg von oben bis zu diesem Dokument – für die Brotkrumen. Das Dokument selbst steht am Ende. */
export function pfadZu<T extends Knoten>(eintraege: readonly T[], id: string): T[] {
  const nachId = new Map(eintraege.map((e) => [e.id, e]))
  const pfad: T[] = []
  let aktuell = nachId.get(id)
  const gesehen = new Set<string>()
  while (aktuell && !gesehen.has(aktuell.id)) {
    gesehen.add(aktuell.id)
    pfad.unshift(aktuell)
    aktuell = aktuell.elternId ? nachId.get(aktuell.elternId) : undefined
  }
  return pfad
}

/**
 * **Die Kette zu einer Datei** – „Mit Übergeordneten kopieren".
 *
 * Jedes Glied bekommt seinen Titel als Überschrift, dazwischen ein Trennstrich. Von oben nach
 * unten, so wie der Baum gelesen wird: erst, was überall gilt, dann, was hier gilt.
 */
export function ketteZusammensetzen(
  glieder: readonly { titel: string; inhalt: string | null }[],
): string {
  return glieder
    .map((g) => `# ${g.titel}\n\n${(g.inhalt ?? '').trim()}`)
    .join('\n\n---\n\n')
    .concat('\n')
}

/** 12,4 MB statt 13002342 – für die Dateizeile. */
export function groesseLesbar(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toLocaleString('de-DE', { maximumFractionDigits: 1 })} MB`
}

/** „10.09.2026, 14:02" */
export function zeitpunktLesbar(wert: Date | string): string {
  return new Date(wert).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}
